import {
  glyphEm,
  wrapDisplayLine,
  wrapDisplayLineEm,
  wrapDisplayLineEmChars,
} from "../../../../src/lib/text/wrap-display-line";
import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { createTikTokStyleCaptions } from "@remotion/captions";
import { getCaptionTokenDisplayText } from "../../vendor/src/shared/lib/captions/caption-token-display-text";
import {
  splitCaptionsByGap,
  CAPTION_GAP_SPLIT_THRESHOLD_MS,
} from "../../vendor/src/features/remotion-editor/editor/items/captions/split-captions-by-gap";

/** Explicit Waka migration metadata; native company captions without this field are untouched. */
export interface WakaSourceTypography {
  bar?: import('../../protocol/source-caption-bar').SourceCaptionBar;
  preserveFontSize: true;
  verticalAlign: "top" | "center";
  whiteSpace: "pre-line";
  textShadow?: string;
  wordBreak?: "keep-all" | "break-all";
  wrap?: { mode: "em" | "legacy" | "free"; maxEm?: number };
  referenceWidth?: number;
  referenceFontSize?: number;
}
export function sourceDisplay(
  text: string,
  width: number,
  size: number,
  source: WakaSourceTypography,
) {
  const wrap = source.wrap;
  if (!wrap) return text;
  const available = Math.max(1, width - 2*(source.bar?.paddingX??0)) / Math.max(1, size);
  const ratio =
    (width / (source.referenceWidth ?? width)) *
    ((source.referenceFontSize ?? size) / size);
  const limit = Math.min(
    available,
    wrap.maxEm && wrap.maxEm > 0 ? wrap.maxEm * ratio : available,
  );
  const display = text
    .split("\n")
    .map((line) => {
      const folded =
        wrap.mode === "em"
          ? wrapDisplayLineEm(line, limit)
          : wrap.mode === "free"
            ? wrapDisplayLineEmChars(line, limit)
            : wrap.maxEm == null
              ? wrapDisplayLine(line)
              : wrapDisplayLine(line, wrap.maxEm * ratio);
      return folded
        .split("\n")
        .map((row) => wrapDisplayLineEmChars(row, available))
        .join("\n");
    })
    .join("\n");
  const rows=display.split("\n"),cap=source.bar?.maxRows;
  return cap && rows.length>cap ? [...rows.slice(0,cap-1),rows.slice(cap-1).join(" ")].join("\n") : display;
}
type RenderToken = {
  key: string;
  text: string;
  active: boolean;
  fillRatio: number;
};
/** Split display spans only: raw caption assets and word timing remain unchanged. */
export function sourceTokenRows<T extends RenderToken>(
  tokens: readonly T[],
  width: number,
  size: number,
  source: WakaSourceTypography,
): T[][] {
  if (!tokens.length) return [];
  const chars = tokens.flatMap((token, index) =>
    Array.from(token.text).map((char) => ({ char, index })),
  );
  const text = chars.map((entry) => entry.char).join("");
  const lines = sourceDisplay(text, width, size, source).split("\n");
  const total = tokens.map((token) =>
    Array.from(token.text).reduce((sum, char) => sum + glyphEm(char), 0),
  );
  const used = tokens.map(() => 0);
  let cursor = 0;
  return lines.map((line, row) => {
    const runs: Array<{
      token: number;
      text: string;
      offset: number;
      width: number;
    }> = [];
    for (const char of Array.from(line)) {
      while (
        cursor < chars.length &&
        /\s/.test(chars[cursor]!.char) &&
        !/\s/.test(char)
      ) {
        const skipped = chars[cursor++]!;
        used[skipped.index]! += glyphEm(skipped.char);
      }
      const entry = chars[cursor];
      const index = entry?.index ?? Math.max(0, tokens.length - 1);
      const offset = used[index] ?? 0;
      if (
        entry &&
        (entry.char === char || (/\s/.test(entry.char) && /\s/.test(char)))
      )
        cursor++;
      const advance = glyphEm(char);
      used[index] = offset + advance;
      const previous = runs.at(-1);
      if (previous?.token === index) {
        previous.text += char;
        previous.width += advance;
      } else runs.push({ token: index, text: char, offset, width: advance });
    }
    if (!runs.length)
      return [
        {
          ...tokens[0]!,
          key: `blank:${row}`,
          text: "\u00a0",
          active: false,
          fillRatio: 0,
        },
      ];
    return runs.map((run, index) => ({
      ...tokens[run.token]!,
      key: `${tokens[run.token]!.key}:row:${row}:${index}`,
      text: run.text,
      fillRatio: Math.max(
        0,
        Math.min(
          1,
          (tokens[run.token]!.fillRatio * (total[run.token] ?? 0) -
            run.offset) /
            Math.max(0.01, run.width),
        ),
      ),
    }));
  });
}

function sameDecoration(left:object|null|undefined,right:object|null|undefined):boolean{
  const a=Object.fromEntries(Object.entries(left??{})),b=Object.fromEntries(Object.entries(right??{}));
  return [...new Set([...Object.keys(a),...Object.keys(b)])].every(key=>a[key]===b[key]);
}

/** Keep canvas/selection geometry in the saved state when a source caption is edited. */
export function normalizeWakaCaptionGeometry(
  previous: EditorState,
  next: EditorState,
): EditorState {
  let changed = false;
  const items = { ...next.undoableState.items };
  for (const original of Object.values(items)) {
    let item=original;
    if (item.type !== "captions" || !item.sourceTypography) continue;
    const before = previous.undoableState.items[item.id];
    if(before?.type==='captions' && item.sourceTypography.bar && (!sameDecoration(before.background,item.background) || (before.backgroundVisible??true)!==(item.backgroundVisible??true))){
      const {bar:_bar,...source}=item.sourceTypography;item={...item,sourceTypography:source};items[item.id]=item;changed=true;
    }
    if(before?.type==='captions' && item.sourceTypography?.textShadow && !sameDecoration(before.shadow,item.shadow)){
      const {textShadow:_shadow,...source}=item.sourceTypography;item={...item,sourceTypography:source};items[item.id]=item;changed=true;
    }
    if (
      before?.type === "captions" &&
      (before.maxLines !== item.maxLines ||
        before.captionPresentationNextItemId !==
          item.captionPresentationNextItemId)
    ) {
      const { sourceTypography: _source, ...native } = item;
      items[item.id] = native;
      changed = true;
      continue;
    }
    const asset = next.undoableState.assets[item.assetId];
    if (asset?.type !== "caption") continue;
    if (
      before?.type === "captions" &&
      before.width === item.width &&
      before.height === item.height &&
      before.fontSize === item.fontSize &&
      before.lineHeight === item.lineHeight &&
      before.pageDurationInMilliseconds === item.pageDurationInMilliseconds &&
      before.assetId === item.assetId &&
      before.sourceTypography === item.sourceTypography &&
      previous.undoableState.assets[item.assetId] === asset
    )
      continue;
    const pages = splitCaptionsByGap(
      asset.captions,
      CAPTION_GAP_SPLIT_THRESHOLD_MS,
    ).flatMap(
      (group) =>
        createTikTokStyleCaptions({
          captions: group,
          combineTokensWithinMilliseconds: item.pageDurationInMilliseconds,
        }).pages,
    );
    const lines = Math.max(
      1,
      ...pages.map(
        (page) =>
          sourceDisplay(
            page.tokens
              .map((_, index) => getCaptionTokenDisplayText(page.tokens, index))
              .join(""),
            item.width,
            item.fontSize,
            item.sourceTypography!,
          ).split("\n").length,
      ),
    );
    const height = lines * item.fontSize * item.lineHeight + 2*(item.sourceTypography?.bar?.paddingY??0);
    if (Math.abs(height - item.height) > 0.001) {
      items[item.id] = { ...item, height, ...(item.sourceTypography?.bar?{top:item.top+(item.height-height)/2}:{}) };
      changed = true;
    }
  }
  return changed
    ? { ...next, undoableState: { ...next.undoableState, items } }
    : next;
}
